import { Box, Stack } from '@mui/material';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { spaces } from '../../../assets/styles/themes/tokens';
import { filterLabels, searchSettings } from '../../../constants';
import useQueryModification from '../../../routes/SearchPage/useQueryModification';
import { SearchFiltersDialogContext } from '../../../state/SearchFiltersDialogProvider';
import type { SearchFilters } from '../../../types/search';
import useRouter from '../../../utils/useRouter';
import useScreenSize from '../../../utils/useScreenSize';
import type { AccordionType } from '../../modals/SearchFiltersDialog/SearchFiltersDialog';
import SortByFilterNewUI from '../../SearchFilters/SortByFilterNewUI';
import MoreFiltersButton from './MoreFiltersButton';
import SearchFilterButton from './SearchFilterButton';

type FilterKey = Extract<AccordionType, keyof SearchFilters>;

interface SearchFiltersContainerProps {
	requestFilters: SearchFilters;
	visibleKeys: Array<FilterKey>;
	invisibleKeys: Array<FilterKey>;
	dialogFiltersOrder: Array<AccordionType>;
	dialogFiltersType: 'core' | 'course';
}

const getSelectionCount = (value: SearchFilters[FilterKey]): number => {
	if (Array.isArray(value)) return value.length;
	if (typeof value === 'boolean') return value ? 1 : 0;
	if (typeof value === 'string') return 1;
	return 0;
};

const SearchFiltersContainer: React.FC<SearchFiltersContainerProps> = (props) => {
	const { requestFilters, visibleKeys, invisibleKeys, dialogFiltersOrder, dialogFiltersType } =
		props;

	const { t } = useTranslation();
	const { router, match } = useRouter();
	const { isXsScreen } = useScreenSize();
	const { updateQueryParams } = useQueryModification<SearchFilters>(router, match);
	const { openSearchFiltersDialog } = useContext(SearchFiltersDialogContext);

	const allInvisibleKeys = isXsScreen ? [...invisibleKeys, ...visibleKeys] : invisibleKeys;

	const numberOfInvisibleFiltersSelected = allInvisibleKeys.reduce((acc, key: FilterKey) => {
		const value = requestFilters[key];
		if (Array.isArray(value)) {
			return acc + value.length;
		}
		if (typeof value === 'boolean') {
			return acc + (value ? 1 : 0);
		}
		if (typeof value === 'string') {
			return acc + 1;
		}
		return acc;
	}, 0);

	const moreFiltersButton = (
		<MoreFiltersButton
			numberOfSelections={numberOfInvisibleFiltersSelected}
			onClick={() => openSearchFiltersDialog(undefined, dialogFiltersOrder, dialogFiltersType)}
		/>
	);

	return (
		<Stack
			direction="row"
			data-testid="search-filters"
			sx={{
				display: 'flex',
				alignItems: 'center',
				alignContent: 'flex-start',
				gap: spaces.xs,
				alignSelf: 'stretch',
			}}
		>
			{!isXsScreen && (
				<>
					{visibleKeys.map((key) => (
						<SearchFilterButton
							key={key}
							text={t(filterLabels[key])}
							numberOfSelections={getSelectionCount(requestFilters[key])}
							onClick={() => openSearchFiltersDialog(key, dialogFiltersOrder, dialogFiltersType)}
						/>
					))}
				</>
			)}
			<Box
				sx={{
					flex: 1,
				}}
			>
				{moreFiltersButton}
			</Box>
			{isXsScreen && dialogFiltersType === 'core' && (
				<Box
					data-testid="search-filters__sort-by"
					sx={{
						display: 'flex',
						flex: 1,
						alignSelf: 'stretch',
						minWidth: 0,
						'& .MuiInputBase-root': {
							height: '100%',
							width: 'unset !important',
						},
						'& .MuiFormControl-root': {
							height: '100%',
							width: '100%',
						},
						'& .MuiListItemText-primary': {
							maxWidth: 'unset !important',
						},
					}}
				>
					<SortByFilterNewUI
						currentValue={requestFilters.sortBy}
						updateFilter={(sortBy) => updateQueryParams({ sortBy })}
						clearFilter={() => updateQueryParams({ sortBy: searchSettings.defaultSortBy })}
					/>
				</Box>
			)}
		</Stack>
	);
};

export default SearchFiltersContainer;
